import { useScreenMessage } from '@gogame/screens';

import { useMutation } from './useMutation';

// const IN_PROGRESS = 0;
const DONE = 1;
const HAS_ERRORS = 2;
const FAILED = 3;
const QUEUED = 4;

const defaultIsCompleted = (response) => response?.status === DONE;

const defaultIsHasErrors = (response) => response?.status === HAS_ERRORS;

const defaultIsFailed = (response) => response?.status === FAILED;

const defaultIsQueued = (response) => response?.status === QUEUED;

const defaultGetProcessMessage = () => {
  throw new Error(
    'Please define the getProcessMessage for useRequestProcessing'
  );
};

const defaultGetCompletedMessage = () => {
  throw new Error(
    'Please define the getCompletedMessage for useRequestProcessing'
  );
};

const defaultGetHasErrorsMessage = () => {
  throw new Error(
    'Please define the getHasErrorsMessage for useRequestProcessing'
  );
};

const defaultGetFailedMessage = () => {
  throw new Error(
    'Please define the getFailedMessage for useRequestProcessing'
  );
};

const defaultGetQueuedMessage = () => {
  throw new Error(
    'Please define the getQueuedMessage for useRequestProcessing'
  );
};

const getMessageArgs = (title, response) => {
  const { error, errors, message, messages, resultMessage } = response || {};
  const info = [
    error,
    message,
    resultMessage,
    ...(errors || []),
    ...(messages?.map((m) => m?.text || m) || []),
  ]
    .filter((i) => i)
    .reduce((acc, i) => (acc.includes(i) ? acc : [...acc, i]), []);

  return info.length
    ? [
        info,
        {
          multiple: true,
          multipleTitle: title,
        },
      ]
    : [title];
};

export const useRequestProcessing = (
  requestKey,
  requestFn,
  requestUpdateStatus,
  {
    isCompleted = defaultIsCompleted,
    isHasErrors = defaultIsHasErrors,
    isFailed = defaultIsFailed,
    isQueued = defaultIsQueued,

    onStart,
    onProcess,
    onCompleted,
    onHasErrors,
    onFailed,
    onQueued,

    getProcessMessage = defaultGetProcessMessage,
    getCompletedMessage = defaultGetCompletedMessage,
    getHasErrorsMessage = defaultGetHasErrorsMessage,
    getFailedMessage = defaultGetFailedMessage,
    getQueuedMessage = defaultGetQueuedMessage,

    refresh,
    timeout,
  } = {}
) => {
  const {
    showSuccessMessage,
    showWarningMessage,
    showErrorMessage,
    showMessage,
  } = useScreenMessage(`${requestKey}`);

  let processSnackbar;

  const start = pendingRequest({
    source: requestUpdateStatus,
    timeout,
    onError: (e) => {
      showErrorMessage(...getMessageArgs(getFailedMessage(e), e));
      processSnackbar.dismiss();
      return true;
    },
    onUpdate: (response, params) => {
      if (!response) {
        showErrorMessage(getFailedMessage(response));
        processSnackbar.dismiss();
        return false;
      } else if (isCompleted(response)) {
        onCompleted?.(response, params);
        showSuccessMessage(getCompletedMessage());
        processSnackbar.dismiss();
        return false;
      } else if (isHasErrors(response)) {
        onHasErrors?.(response, params);
        showWarningMessage(
          ...getMessageArgs(getHasErrorsMessage(response), response)
        );
        processSnackbar.dismiss();
        return false;
      } else if (isFailed(response)) {
        onFailed?.(response, params);
        showErrorMessage(
          ...getMessageArgs(getFailedMessage(response), response)
        );
        processSnackbar.dismiss();
        return false;
      } else if (isQueued(response)) {
        onQueued?.(response, params);
        processSnackbar.update(getQueuedMessage(response), response);
        return true;
      }

      onProcess?.(response, params);
      processSnackbar.update(getProcessMessage(response));
      return true;
    },
  });

  const updateMutation = useMutation(requestKey, requestFn, {
    refresh,
    successMessage: null,
    onSuccess: (response, params) => {
      processSnackbar = showMessage(getProcessMessage(), {
        ttl: 1000 * 60 * 30,
      });
      onStart?.(response, params);
      start(response);
    },
  });
  return [updateMutation.mutateAsync, updateMutation];
};

function pendingRequest({
  source,
  onUpdate,
  onError,
  timeout = 3000,
  errorTtl = 5,
}) {
  const checkResult = async (params) => {
    let shouldRepeat;
    let result = null;
    let error = null;
    try {
      result = await source(params);
      shouldRepeat = onUpdate(result, params);
    } catch (e) {
      error = e;
      if (errorTtl--) {
        shouldRepeat = onError(e);
      } else {
        shouldRepeat = false;
      }
    }

    return new Promise((resolve, reject) => {
      if (shouldRepeat) {
        setTimeout(
          () => checkResult(params).then(resolve).catch(reject),
          timeout
        );
      } else if (error) {
        reject(error);
      } else {
        resolve(result);
      }
    });
  };

  return checkResult;
}
